<template>
  <div class="asc-root">
    <div v-if="!flagResolved" class="asc-state-message" data-testid="asc-loading">
      Loading Adoption scoring…
    </div>

    <div
      v-else-if="!featureEnabled"
      class="asc-state-message asc-state-message--disabled"
      data-testid="asc-feature-disabled"
    >
      <v-icon aria-hidden="true">mdi-flag-outline</v-icon>
      <p class="asc-state-message__title">Adoption scoring is not enabled</p>
      <p class="asc-state-message__body">
        Turn on LaunchDarkly flag <strong>idea-MDP-627</strong> (Account Adoption) for your user or
        environment, then reload this page.
      </p>
    </div>

    <div v-else-if="!isSuperUser" class="asc-state-message" data-testid="asc-restricted">
      <v-icon aria-hidden="true">mdi-lock-outline</v-icon>
      <p class="asc-state-message__title">You do not have permission to view this page.</p>
      <p class="asc-state-message__body">
        Adoption scoring settings are restricted to platform sysadmins.
      </p>
    </div>

    <div v-else class="asc-page" data-testid="adoption-scoring-page">
      <!-- Header -->
      <header class="asc-header">
        <div class="asc-title-block">
          <ExBreadcrumbs :breadcrumbs-list="breadcrumbs" />
          <div class="asc-title-row">
            <h1 class="asc-title">Adoption scoring</h1>
            <span class="asc-status-badge" data-testid="asc-status-badge">{{ statusBadge }}</span>
          </div>
          <p class="asc-supporting">
            How the Stickiness score, bands and signals are calculated for every account. Saving
            creates a new version; scores are recalculated from its effective date and older scores
            keep the version they were calculated with.
          </p>
        </div>

        <div class="asc-actions">
          <button
            type="button"
            class="asc-btn asc-btn--cancel"
            data-testid="asc-cancel"
            @click="onCancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="asc-btn asc-btn--save"
            :disabled="!canSave || saving"
            data-testid="asc-save"
            @click="onSave"
          >
            Save as new version
          </button>
        </div>
      </header>

      <!-- Two columns -->
      <div class="asc-columns">
        <div class="asc-left">
          <!-- Score weights -->
          <section class="asc-card" data-testid="score-weights-card">
            <header class="card-header">
              <h2 class="card-title">Score weights</h2>
              <p class="card-subtitle">
                How much each dimension counts toward the Stickiness score. Must add up to 100%.
              </p>
            </header>

            <div
              v-for="row in SCORE_WEIGHT_ROWS"
              :key="row.key"
              class="weight-row"
              :data-testid="`score-weight-${row.key}`"
            >
              <div class="weight-dim">
                <span class="weight-label">{{ row.label }}</span>
                <span class="weight-desc">{{ row.description }}</span>
              </div>
              <div class="weight-input">
                <v-text-field
                  v-model.number="draft.scoreWeights[row.key]"
                  :disabled="readOnly"
                  type="number"
                  min="0"
                  max="100"
                  hide-details
                  dense
                  outlined
                  suffix="%"
                  :aria-label="`${row.label} weight`"
                  :data-testid="`score-weight-input-${row.key}`"
                />
              </div>
            </div>

            <div class="weight-total">
              <span class="weight-total-value" data-testid="score-weight-total">
                Total {{ weightsTotal }}%
              </span>
              <span
                class="severity-badge"
                :class="weightsValid ? 'severity-badge--green' : 'severity-badge--red'"
                :data-testid="weightsValid ? 'score-weights-valid' : 'score-weights-invalid'"
              >
                {{ weightsValid ? "Adds up to 100%" : `${weightsTotal - 100}% off 100%` }}
              </span>
            </div>
          </section>

          <!-- Action weights -->
          <section class="asc-card" data-testid="action-weights-card">
            <header class="card-header">
              <h2 class="card-title">Action weights (Depth)</h2>
              <p class="card-subtitle">
                How much each action counts toward Depth. Bigger actions count more, so clicking
                through easy tasks can’t inflate the score.
              </p>
            </header>

            <div
              v-for="row in ACTION_WEIGHT_ROWS"
              :key="row.key"
              class="weight-row"
              :data-testid="`action-weight-${row.key}`"
            >
              <div class="weight-dim">
                <span class="weight-label">{{ row.label }}</span>
                <span class="weight-desc">{{ row.description }}</span>
              </div>
              <div class="weight-input">
                <v-text-field
                  v-model.number="draft.actionWeights[row.key]"
                  :disabled="readOnly"
                  type="number"
                  min="0"
                  hide-details
                  dense
                  outlined
                  suffix="pts"
                  :aria-label="`${row.label} points`"
                  :data-testid="`action-weight-input-${row.key}`"
                />
              </div>
            </div>
          </section>

          <!-- Score scaling -->
          <section class="asc-card" data-testid="score-scaling-card">
            <header class="card-header">
              <h2 class="card-title">Score scaling</h2>
              <p class="card-subtitle">
                The level that earns a full 100 on Depth and Value. Set it so a strong account
                scores near 100.
              </p>
            </header>

            <div class="weight-row" data-testid="score-scaling-depth">
              <div class="weight-dim">
                <span class="weight-label">Depth full-score level</span>
                <span class="weight-desc"
                  >Weighted actions per 100 employees that earn a full 100</span
                >
              </div>
              <div class="weight-input">
                <v-text-field
                  v-model.number="draft.depthFullScoreLevel"
                  :disabled="readOnly"
                  type="number"
                  min="0"
                  hide-details
                  dense
                  outlined
                  suffix="pts"
                  aria-label="Depth full-score level"
                  data-testid="score-scaling-depth-input"
                />
              </div>
            </div>

            <div class="weight-row" data-testid="score-scaling-value">
              <div class="weight-dim">
                <span class="weight-label">Value full-score level</span>
                <span class="weight-desc">Badges per 100 employees that earn a full 100</span>
              </div>
              <div class="weight-input">
                <v-text-field
                  v-model.number="draft.valueFullScoreLevel"
                  :disabled="readOnly"
                  type="number"
                  min="0"
                  hide-details
                  dense
                  outlined
                  suffix="badges"
                  aria-label="Value full-score level"
                  data-testid="score-scaling-value-input"
                />
              </div>
            </div>

            <p
              v-if="!fullScoreLevelsValid"
              class="asc-validation-error"
              data-testid="score-scaling-error"
            >
              Full-score levels must be non-negative integers.
            </p>
          </section>

          <!-- Score bands -->
          <section class="asc-card" data-testid="score-bands-card">
            <header class="card-header">
              <h2 class="card-title">Score bands</h2>
              <p class="card-subtitle">
                Which label an account gets for its score. Watch is everything between the two
                cut-offs.
              </p>
            </header>

            <div class="band-scale" data-testid="band-scale">
              <span
                v-for="band in bandScale"
                :key="band.label"
                class="band-segment"
                :style="{ background: band.color, color: band.textColor, width: band.width }"
              >
                {{ band.label }} {{ band.range }}
              </span>
            </div>

            <div class="band-inputs">
              <div class="band-field">
                <span class="weight-label">Sticky</span>
                <span class="weight-desc">at or above</span>
                <v-text-field
                  v-model.number="draft.bandCutoffs.stickyAtOrAbove"
                  :disabled="readOnly"
                  type="number"
                  min="0"
                  max="100"
                  hide-details
                  dense
                  outlined
                  aria-label="Sticky at or above"
                  data-testid="band-sticky-input"
                />
              </div>
              <div class="band-field">
                <span class="weight-label">Stalling</span>
                <span class="weight-desc">below</span>
                <v-text-field
                  v-model.number="draft.bandCutoffs.stallingBelow"
                  :disabled="readOnly"
                  type="number"
                  min="0"
                  max="100"
                  hide-details
                  dense
                  outlined
                  aria-label="Stalling below"
                  data-testid="band-stalling-input"
                />
              </div>
            </div>

            <p v-if="!bandsValid" class="asc-validation-error" data-testid="band-validation-error">
              "Stalling below" must be lower than "Sticky at or above".
            </p>
          </section>

          <!-- Signal rules -->
          <SignalRulesTable
            :rules="draft.signalRules"
            :read-only="readOnly"
            @update:rules="draft.signalRules = $event"
          />
        </div>

        <div class="asc-right">
          <!-- Version history -->
          <section class="asc-card" data-testid="version-history-card">
            <header class="card-header">
              <h2 class="card-title">Version history</h2>
              <p class="card-subtitle">Each account’s score shows which version produced it.</p>
            </header>

            <div
              v-for="version in versions"
              :key="version.id || version.version"
              class="version-row"
              :data-testid="`version-row-${version.id || version.version}`"
            >
              <div class="version-meta">
                <span class="version-label">
                  {{ version.version }}
                  <span
                    v-if="version.in_force"
                    class="severity-badge severity-badge--green"
                    data-testid="version-current-badge"
                  >
                    Current
                  </span>
                </span>
                <span
                  class="version-sub"
                  :data-testid="`version-sub-${version.id || version.version}`"
                >
                  {{ versionSubtitle(version) }}
                </span>
              </div>
              <button
                v-if="!version.in_force"
                type="button"
                class="version-view"
                :data-testid="`version-view-${version.id || version.version}`"
                @click="viewVersion(version.id)"
              >
                View
              </button>
            </div>

            <p v-if="!versions.length" class="version-empty" data-testid="version-history-empty">
              No versions yet.
            </p>
          </section>

          <!-- Impact preview -->
          <AdoptionImpactPreview :draft="draftPayload" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import { LD_FLAG_IDEA_MDP_627 } from "@components/constants/launchDarklyFlagKeys";
import SignalRulesTable from "@/components/SignalRulesTable.vue";
import AdoptionImpactPreview from "@/components/AdoptionImpactPreview.vue";

import { getScoringConfigs, getScoringConfig, createScoringConfig } from "@/api";
import { isAdminSysadmin } from "@/utils/isAdminSysadmin";

/**
 * ER-12307 — Admin Portal Settings > Adoption scoring (MDP-627).
 *
 * Versioned, effective-dated scoring configuration: score weights, action
 * weights, Depth/Value full-score levels, band cut-offs and signal rules.
 * "Save as new version" creates a new row; past versions open read-only.
 * Sysadmin-only and gated behind idea-MDP-627.
 */

const proxy = getCurrentInstance()?.proxy;

const SCORE_WEIGHT_ROWS = [
  {
    key: "breadth",
    label: "Breadth",
    description: "Active people: signed in and completed ≥ 1 action",
  },
  { key: "depth", label: "Depth", description: "Weighted actions per 100 employees" },
  {
    key: "value",
    label: "Value",
    description: "Badges earned · employees with a current pay plan",
  },
  {
    key: "momentum",
    label: "Momentum",
    description: "Last 30 days vs the account’s 90-day average",
  },
];

const ACTION_WEIGHT_ROWS = [
  {
    key: "task_completed",
    label: "Task completed",
    description: "Any task an employee or coach finishes",
  },
  {
    key: "checkpoint_evaluated",
    label: "Checkpoint evaluated",
    description: "A coach evaluates an employee on a checkpoint",
  },
  {
    key: "inspection_completed",
    label: "Inspection completed",
    description: "A coach completes an inspection protocol",
  },
  {
    key: "badge_awarded",
    label: "Badge awarded",
    description: "An employee earns a readiness badge",
  },
  {
    key: "pay_plan_proposal_committed",
    label: "Pay plan proposal committed",
    description: "A Fair Pay proposal is committed",
  },
  {
    key: "pay_increase_campaign_committed",
    label: "Pay increase campaign committed",
    description: "A whole campaign is committed",
  },
];

const SEED_SIGNAL_RULES = [
  {
    key: "owner_or_admin_inactive",
    label: "Owner or admin inactive",
    threshold: 14,
    unit: "days without signing in",
    hasThreshold: true,
    severity: "critical",
    active: true,
    playbookUrl: "",
  },
  {
    key: "no_badge_awarded_in_department",
    label: "No badge awarded in a department",
    threshold: 45,
    unit: "days",
    hasThreshold: true,
    severity: "warning",
    active: true,
    playbookUrl: "",
  },
  {
    key: "overdue_task_rate_rises",
    label: "Overdue-task rate rises",
    threshold: 10,
    unit: "points in the period",
    hasThreshold: true,
    severity: "warning",
    active: true,
    playbookUrl: "",
  },
  {
    key: "active_employees_drop",
    label: "Active employees drop",
    threshold: 15,
    unit: "points in the period",
    hasThreshold: true,
    severity: "warning",
    active: true,
    playbookUrl: "",
  },
  {
    key: "pay_cycle_missed",
    label: "Pay cycle missed (campaign or proposal)",
    threshold: 30,
    unit: "days past due",
    hasThreshold: true,
    severity: "warning",
    active: true,
    playbookUrl: "",
  },
  {
    key: "positions_below_target",
    label: "Positions with roles & responsibilities below target",
    threshold: 60,
    unit: "% of positions",
    hasThreshold: true,
    severity: "warning",
    active: true,
    playbookUrl: "",
  },
  {
    key: "action_completed_on_schedule",
    label: "Action completed on schedule",
    threshold: null,
    unit: "",
    hasThreshold: false,
    severity: "positive",
    active: true,
    playbookUrl: "",
  },
];

function defaultDraft() {
  return {
    scoreWeights: { breadth: 30, depth: 35, value: 25, momentum: 10 },
    actionWeights: {
      task_completed: 1,
      checkpoint_evaluated: 2,
      inspection_completed: 3,
      badge_awarded: 3,
      pay_plan_proposal_committed: 3,
      pay_increase_campaign_committed: 5,
    },
    depthFullScoreLevel: 400,
    valueFullScoreLevel: 25,
    bandCutoffs: { stickyAtOrAbove: 70, stallingBelow: 40 },
    signalRules: SEED_SIGNAL_RULES.map((r) => ({ ...r })),
  };
}

const breadcrumbs = [{ text: "Settings" }, { text: "Adoption scoring", disabled: true }];

const flagResolved = ref(false);
const featureEnabled = ref(false);
const isSuperUser = ref(false);

const versions = ref([]);
const draft = ref(defaultDraft());
const inForceConfig = ref(defaultDraft());
const viewingVersionId = ref(null);
const saving = ref(false);

const readOnly = computed(() => viewingVersionId.value !== null);

const weightsTotal = computed(() =>
  Object.values(draft.value.scoreWeights).reduce((sum, n) => sum + Number(n || 0), 0)
);

const weightsValid = computed(() => weightsTotal.value === 100);

const bandsValid = computed(
  () =>
    Number(draft.value.bandCutoffs.stallingBelow) < Number(draft.value.bandCutoffs.stickyAtOrAbove)
);

const fullScoreLevelsValid = computed(() => {
  const d = Number(draft.value.depthFullScoreLevel);
  const v = Number(draft.value.valueFullScoreLevel);
  return Number.isInteger(d) && d >= 0 && Number.isInteger(v) && v >= 0;
});

const canSave = computed(
  () => weightsValid.value && bandsValid.value && fullScoreLevelsValid.value && !readOnly.value
);

// Dark text keeps every band label at >= 4.5:1 (4.72 on #f04438, 6.77 on #12b76a, 7.56 on #f79009).
const BAND_TEXT_COLOR = "#101828";

const bandScale = computed(() => {
  const sticky = Number(draft.value.bandCutoffs.stickyAtOrAbove);
  const stalling = Number(draft.value.bandCutoffs.stallingBelow);
  const stallingPct = Math.max(0, Math.min(100, stalling));
  const watchPct = Math.max(0, Math.min(100, sticky - stalling));
  const stickyPct = Math.max(0, 100 - stallingPct - watchPct);
  return [
    {
      label: "Stalling",
      range: `0–${Math.max(0, stalling - 1)}`,
      color: "#f04438",
      textColor: BAND_TEXT_COLOR,
      width: `${stallingPct}%`,
    },
    {
      label: "Watch",
      range: `${stalling}–${Math.max(stalling, sticky - 1)}`,
      color: "#f79009",
      textColor: BAND_TEXT_COLOR,
      width: `${watchPct}%`,
    },
    {
      label: "Sticky",
      range: `${sticky}–100`,
      color: "#12b76a",
      textColor: BAND_TEXT_COLOR,
      width: `${stickyPct}%`,
    },
  ];
});

const versionSubtitle = (version) => {
  const author = `Saved by ${version.author || "—"}`;
  return version.effective_date ? `${author} · ${version.effective_date}` : author;
};

const statusBadge = computed(() => {
  const current = versions.value.find((v) => v.in_force);
  if (!current) return "v1 · effective —";
  return `${current.version} · effective ${current.effective_date || ""}`.trim();
});

const draftPayload = computed(() => ({
  score_weights: { ...draft.value.scoreWeights },
  action_weights: { ...draft.value.actionWeights },
  depth_full_score_level: draft.value.depthFullScoreLevel,
  value_full_score_level: draft.value.valueFullScoreLevel,
  band_cutoffs: {
    sticky_at_or_above: draft.value.bandCutoffs.stickyAtOrAbove,
    stalling_below: draft.value.bandCutoffs.stallingBelow,
  },
  signal_rules: draft.value.signalRules.map((r) => ({
    key: r.key,
    threshold: r.hasThreshold ? r.threshold : null,
    severity: r.severity,
    active: r.active,
    playbook_url: r.playbookUrl || "",
  })),
}));

const isDirty = computed(
  () => JSON.stringify(draftPayload.value) !== JSON.stringify(payloadOf(inForceConfig.value))
);

function payloadOf(cfg) {
  return {
    score_weights: { ...cfg.scoreWeights },
    action_weights: { ...cfg.actionWeights },
    depth_full_score_level: cfg.depthFullScoreLevel,
    value_full_score_level: cfg.valueFullScoreLevel,
    band_cutoffs: {
      sticky_at_or_above: cfg.bandCutoffs.stickyAtOrAbove,
      stalling_below: cfg.bandCutoffs.stallingBelow,
    },
    signal_rules: cfg.signalRules.map((r) => ({
      key: r.key,
      threshold: r.hasThreshold ? r.threshold : null,
      severity: r.severity,
      active: r.active,
      playbook_url: r.playbookUrl || "",
    })),
  };
}

function readIsSuperUser() {
  return isAdminSysadmin();
}

function hydrate(config) {
  const rules = (config.signal_rules || config.signalRules || []).map((r) => ({
    key: r.key,
    label: r.label,
    threshold: r.threshold,
    unit: r.unit,
    hasThreshold:
      r.hasThreshold ?? r.has_threshold ?? (r.threshold !== null && r.threshold !== undefined),
    severity: r.severity,
    active: r.active,
    playbookUrl: r.playbook_url ?? r.playbookUrl ?? "",
  }));
  return {
    scoreWeights: { ...(config.score_weights || config.scoreWeights || {}) },
    actionWeights: { ...(config.action_weights || config.actionWeights || {}) },
    depthFullScoreLevel: config.depth_full_score_level ?? config.depthFullScoreLevel ?? 400,
    valueFullScoreLevel: config.value_full_score_level ?? config.valueFullScoreLevel ?? 25,
    bandCutoffs: {
      stickyAtOrAbove:
        config.band_cutoffs?.sticky_at_or_above ?? config.bandCutoffs?.stickyAtOrAbove ?? 70,
      stallingBelow: config.band_cutoffs?.stalling_below ?? config.bandCutoffs?.stallingBelow ?? 40,
    },
    signalRules: rules,
  };
}

function applyVersion(version) {
  draft.value = hydrate(version);
  inForceConfig.value = hydrate(version);
  viewingVersionId.value = null;
}

async function loadVersions() {
  try {
    const response = await getScoringConfigs();
    const list = Array.isArray(response?.data) ? response.data : [];
    versions.value = [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
    const current = versions.value.find((v) => v.in_force);
    if (current) {
      applyVersion(current);
    } else if (!versions.value.length) {
      // Backend not yet seeded (ER-12305) — fall back to version 1 defaults.
      versions.value = [
        { id: null, version: "v1", effective_date: "", author: "—", in_force: true },
      ];
    }
  } catch (error) {
    console.error("Failed to load adoption scoring versions:", error);
  }
}

async function viewVersion(id) {
  if (id == null) return;
  try {
    const response = await getScoringConfig(id);
    if (response?.data) {
      draft.value = hydrate(response.data);
      viewingVersionId.value = id;
    }
  } catch (error) {
    console.error("Failed to load adoption scoring version:", error);
  }
}

function resetToInForce() {
  draft.value = hydrate(inForceConfig.value);
  viewingVersionId.value = null;
}

function onCancel() {
  if (isDirty.value && !readOnly.value) {
    const confirmed = window.confirm("Discard unsaved changes?");
    if (!confirmed) return;
  }
  resetToInForce();
}

async function onSave() {
  if (!canSave.value || saving.value) return;
  saving.value = true;
  try {
    const response = await createScoringConfig(draftPayload.value);
    const created = response?.data || {
      version: `v${versions.value.length + 1}`,
      author: readAuthorName(),
      in_force: true,
      ...draftPayload.value,
    };
    inForceConfig.value = hydrate({ ...draft.value });
    versions.value = [created, ...versions.value.map((v) => ({ ...v, in_force: false }))];
    viewingVersionId.value = null;
    proxy?.$notifications?.success("New version saved.");
  } catch (error) {
    const detail = error?.response?.data?.detail || error?.message || "Something went wrong.";
    proxy?.$notifications?.error(`Save failed: ${detail}`);
  } finally {
    saving.value = false;
  }
}

function readAuthorName() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const user = JSON.parse(raw);
    return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "";
  } catch {
    return "";
  }
}

onMounted(async () => {
  isSuperUser.value = readIsSuperUser();
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_627, false)) === true;
  } catch {
    featureEnabled.value = false;
  } finally {
    flagResolved.value = true;
  }
  if (featureEnabled.value && isSuperUser.value) {
    await loadVersions();
  }
});
</script>

<style scoped>
.asc-root {
  width: 100%;
  min-height: 360px;
}

.asc-state-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 320px;
  padding: 24px;
  text-align: center;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.asc-state-message--disabled .asc-state-message__title {
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: #101828;
}

.asc-state-message__title {
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: #101828;
}

.asc-state-message__body {
  margin: 0;
  max-width: 460px;
}

.asc-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: #ffffff;
  font-family: Inter, sans-serif;
  color: #101828;
}

.asc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.asc-title-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 846px;
}

.asc-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.asc-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #101828;
}

.asc-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid #e9d7fe;
  background: #f9f5ff;
  color: #7f56d9;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.asc-supporting {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.asc-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
}

.asc-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
}

.asc-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.asc-btn--cancel {
  background: #f2edff;
  border-color: #f2edff;
  color: #6941c6;
}

.asc-btn--save {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #ffffff;
}

.asc-btn--save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.asc-columns {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.asc-left {
  flex: 1 1 786px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 786px;
}

.asc-right {
  flex: 0 0 340px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.asc-card {
  width: 100%;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 24px;
  box-sizing: border-box;
}

.card-header {
  margin-bottom: 8px;
}

.card-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.card-subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.weight-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #eaecf0;
}

.weight-dim {
  flex: 1 1 55%;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.weight-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}

.weight-desc {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.weight-input {
  flex: 0 0 140px;
}

.weight-total {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
}

.weight-total-value {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}

.severity-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.severity-badge--green {
  color: #067647;
  background: #ecfdf3;
  border-color: #abefc6;
}

.severity-badge--red {
  color: #b42318;
  background: #fef3f2;
  border-color: #fecdca;
}

.asc-validation-error {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: #b42318;
}

.band-scale {
  display: flex;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
}

.band-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
}

.band-inputs {
  display: flex;
  gap: 24px;
}

.band-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.version-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #eaecf0;
}

.version-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.version-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}

.version-sub {
  font-size: 12px;
  line-height: 18px;
  color: #667085;
}

.version-view {
  border: none;
  background: transparent;
  padding: 4px 8px;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #7f56d9;
}

.version-view:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
  border-radius: 6px;
}

.version-empty {
  margin: 12px 0 0;
  font-size: 14px;
  color: #667085;
}

@media (max-width: 900px) {
  .asc-columns {
    flex-direction: column;
  }
  .asc-left {
    max-width: none;
  }
  .asc-right {
    flex: 1 1 auto;
    width: 100%;
  }
}
</style>
